import { useCallback } from 'react';
import { useEpisodeStore } from '@/state/episodeStore';
import { generateSceneVideo } from '@/services/scenesApi';
import { runMutation } from '@/state/saveQueue';

interface GenerateVideoInput {
  sceneId: string;
  sourceImageId: string;
  prompt: string;
  references: { sceneIds: string[]; castIds: string[] };
  fresh: boolean;
  duration: 4 | 6 | 8;
  model: 'veo-fast' | 'veo-full';
  extendsVideoId?: string;
}

/**
 * Trigger Veo generation for the current scene. Backend polls Veo in a queue
 * worker; this call returns the initial 'generating' row, and the UI should
 * poll GET /v2/scenes/{id}/videos/{vid} for status.
 */
export function useGenerateVideo() {
  return useCallback(async (episodeUuid: string, input: GenerateVideoInput) => {
    const { upsertVideo, scenes, upsertScene } = useEpisodeStore.getState();
    const target = scenes.find((s) => s.id === input.sceneId);
    if (!target) throw new Error('Scene not found.');

    const newVideo = await runMutation(
      {
        episodeUuid,
        endpoint: `/v2/scenes/${input.sceneId}/videos`,
        method: 'POST',
        body: input,
      },
      () =>
        generateSceneVideo(episodeUuid, input.sceneId, {
          sourceImageId: input.sourceImageId,
          prompt: input.prompt,
          duration: input.duration,
          model: input.model,
          extendsVideoId: input.extendsVideoId,
        }),
    );

    upsertVideo(newVideo);
    upsertScene({
      ...target,
      videoIds: [...target.videoIds, newVideo.id],
      version: target.version + 1,
      updatedAt: Date.now(),
    });
    return newVideo;
  }, []);
}
